// UserPlants.jsx
// Admin view to list all plants for a given user ID with level/status/sprite or latest photo

// Packages, dependencies, and library functions
import React, { useCallback, useEffect, useMemo, useState } from "react";
import db from "../../Firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { getImageURLFromDatabase } from "../../DatabaseOperations";

// React Components
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";
import PlantImage from "../../Sprite/PlantImage";

// CSS (reuse existing styles from console editors)
import "../SpeciesInfoUpload/PlantEditor.css";

const UserPlants = () => {
  // Hooks
  const [userId, setUserId] = useState("");
  const [plants, setPlants] = useState([]);
  const [speciesById, setSpeciesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setPlants([]);
    setSpeciesById({});
    setError("");
  };

  const fetchSpeciesIfNeeded = useCallback(
    async (speciesId) => {
      if (!speciesId) return null;
      if (speciesById[speciesId]) return speciesById[speciesId];
      try {
        const snap = await getDoc(doc(db, "Species", speciesId));
        if (snap.exists()) {
          const data = snap.data();
          setSpeciesById((prev) => ({ ...prev, [speciesId]: data }));
          return data;
        }
      } catch (e) {
        // ignore, best-effort
      }
      return null;
    },
    [speciesById]
  );

  const getLatestProgressPhotoUrl = useCallback(async (plantId) => {
    try {
      const photosRef = collection(db, "Plants", plantId, "ProgressPhotos");
      // Try common timestamp fields for ordering; fall back to any doc if needed
      const candidateFields = [
        "createdAt",
        "timestamp",
        "uploadedAt",
        "dateCreated",
        "created",
      ];
      let snap = null;
      for (let i = 0; i < candidateFields.length; i++) {
        try {
          const q = query(
            photosRef,
            orderBy(candidateFields[i], "desc"),
            limit(1)
          );
          // eslint-disable-next-line no-await-in-loop
          snap = await getDocs(q);
          break;
        } catch (e) {
          // Try next field
        }
      }
      if (!snap) {
        snap = await getDocs(photosRef);
      }
      if (!snap.empty) {
        const docData = snap.docs[0].data();
        const urlLike = docData.imageUrl || docData.imageURL || docData.url;
        if (urlLike && typeof urlLike === "string") {
          return urlLike;
        }
        const pathLike =
          docData.imagePath || docData.storagePath || docData.path;
        if (pathLike && typeof pathLike === "string") {
          try {
            const url = await getImageURLFromDatabase(pathLike);
            return url;
          } catch (e) {
            return null;
          }
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  const loadPlants = useCallback(async () => {
    if (!userId || !userId.trim()) {
      setError("Please enter a user ID");
      return;
    }
    setLoading(true);
    setError("");
    setPlants([]);
    try {
      const q = query(
        collection(db, "Plants"),
        where("owner", "==", userId.trim())
      );
      const snap = await getDocs(q);
      const basePlants = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Fetch species and latest photo for each plant in parallel
      const enriched = await Promise.all(
        basePlants.map(async (p) => {
          const [species, latestPhotoUrl] = await Promise.all([
            fetchSpeciesIfNeeded(p.species),
            getLatestProgressPhotoUrl(p.id),
          ]);
          return { ...p, __species: species, __latestPhotoUrl: latestPhotoUrl };
        })
      );
      setPlants(enriched);
    } catch (err) {
      setError(`Failed to load plants: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, fetchSpeciesIfNeeded, getLatestProgressPhotoUrl]);

  const plantCards = useMemo(() => {
    return plants.map((plant) => {
      const hasSprite = !!(
        plant.sprite &&
        plant.sprite.eyes &&
        plant.sprite.mouth &&
        plant.sprite.pot
      );
      const statusParts = [];
      if (typeof plant.level !== "undefined")
        statusParts.push(`Level ${plant.level}`);
      if (typeof plant.stage !== "undefined")
        statusParts.push(`Stage ${plant.stage}`);
      if (typeof plant.health !== "undefined")
        statusParts.push(`Health ${plant.health}`);
      const statusStr = statusParts.join(" · ");

      return (
        <div
          key={plant.id}
          className="plantFieldContainer"
          style={{
            marginBottom: "24px",
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ width: 160, minWidth: 160 }}>
              {hasSprite && plant.__species ? (
                <PlantImage
                  plant={plant}
                  species={plant.__species}
                  onLoadCallback={() => {}}
                  width="160px"
                  height="160px"
                />
              ) : plant.__latestPhotoUrl ? (
                <img
                  src={plant.__latestPhotoUrl}
                  alt={`${plant.name || plant.id} recent`}
                  style={{
                    width: 160,
                    height: 160,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 160,
                    height: 160,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f7f7f7",
                    color: "#999",
                    borderRadius: 8,
                  }}
                >
                  No preview
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div className="plantTextFieldLabel" style={{ marginBottom: 4 }}>
                {plant.name || "Unnamed Plant"}
              </div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                {plant.species} · {plant.id}
              </div>
              {statusStr ? (
                <div style={{ fontSize: 14 }}>{statusStr}</div>
              ) : null}
            </div>
          </div>
        </div>
      );
    });
  }, [plants]);

  useEffect(() => {
    // no-op
  }, []);

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">User Plants</div>
      <div className="plantFieldContainer">
        <label htmlFor="userId" className="plantTextFieldLabel">
          User ID
        </label>
        <input
          id="userId"
          className="textbox plantIDTextbox"
          placeholder="Enter user auth UID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          type="text"
        />
      </div>
      <div style={{ display: "flex", gap: 12, margin: "12px 0 24px" }}>
        <input
          className="getPlantButton"
          type="submit"
          value={loading ? "Loading..." : "Load Plants"}
          onClick={loadPlants}
          disabled={loading}
        />
        <input
          className="getPlantButton"
          type="button"
          value="Reset"
          onClick={reset}
        />
      </div>
      {error !== "" ? (
        <div style={{ color: "red", marginBottom: 12 }}>{error}</div>
      ) : null}
      <div style={{ maxWidth: 900, margin: "0 auto" }}>{plantCards}</div>
      {!loading && plants.length === 0 && !error ? (
        <div style={{ color: "#666" }}>No plants found.</div>
      ) : null}
    </div>
  );
};

export default UserPlants;
